import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import {
  CatalogAccountDto,
  CatalogAccountListResponseDto,
  CatalogAccountPatchRequestDto,
  CatalogAccountsQuery,
  CatalogFolderCreateRequestDto,
  CatalogFolderDto,
  CatalogFolderListResponseDto,
  CatalogFolderPatchRequestDto,
  CatalogItemTypeDto,
  CatalogItemTypeListResponseDto,
  CatalogItemTypePatchRequestDto,
  CatalogItemTypesQuery,
  CatalogPriceTypeDto,
  CatalogPriceTypeListResponseDto,
  CatalogPriceTypePatchRequestDto,
  CatalogWarehouseDto,
  CatalogWarehouseListResponseDto,
  CatalogWarehousePatchRequestDto,
  CatalogWarehousesQuery,
  CatalogItemViewQuery,
  CatalogItemViewResponseDto,
  CatalogItemsQuery,
  CatalogItemsMoveRequestDto,
  CatalogItemsMoveResponseDto,
  CatalogItemsResponseDto,
  CatalogOrderMoveRequestDto,
  CatalogOrderMoveResponseDto,
  CatalogRegistryQuery,
  CatalogRegistryResponseDto,
  Uuid,
} from './catalog.api.types';

import { buildApiUrl } from '../../api/url.util';
import { API_BASE_URL } from '../../api/api.config';

/**
 * Сериализует query-объект в `HttpParams` без локальной бизнес-логики.
 */
function toHttpParams(q: Record<string, unknown>): HttpParams {
  let p = new HttpParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null) continue;

    // Для текущих catalog endpoints массивы не используются, поэтому сериализация остаётся простой.
    p = p.set(k, String(v));
  }
  return p;
}

function toCatalogRegistryHttpParams(query: CatalogRegistryQuery): HttpParams {
  let params = new HttpParams().set('folder_id', query.folder_id);

  for (const warehouseId of query.regular_warehouse_ids) {
    params = params.append('regular_warehouse_ids[]', warehouseId);
  }

  for (const warehouseId of query.supply_warehouse_ids ?? []) {
    params = params.append('supply_warehouse_ids[]', warehouseId);
  }

  for (const itemType of query.item_type ?? []) {
    params = params.append('item_type[]', itemType);
  }

  return params;
}

/**
 * API-слой каталога.
 * Базовый URL берётся из DI, чтобы все сервисы проекта работали через единый источник истины.
 */
@Injectable({ providedIn: 'root' })
export class CatalogApiService {
  private readonly http = inject(HttpClient);

  /**
   * Базовый URL задаётся через DI:
   * - `''` означает относительные запросы через proxy;
   * - `http(s)://...` означает абсолютный backend URL.
   */
  private readonly apiBaseUrl = (inject(API_BASE_URL, { optional: true }) ?? '').trim();

  // Складской каталог и справочники.

  /**
   * Список номенклатуры и остатков для каталога.
   */
  listItems(query: CatalogItemsQuery) {
    const params = toHttpParams(query as Record<string, unknown>);
    return this.http.get<CatalogItemsResponseDto>(this.url('/catalog/items'), { params });
  }

  /**
   * Возвращает карточку модели по ID модели или серии.
   * Сервер сам резолвит дочернюю серию к корневой модели и применяет тот же stock-scope, что и для списка.
   */
  viewItem(id: Uuid, query: CatalogItemViewQuery = {}) {
    const params = toHttpParams(query as Record<string, unknown>);
    return this.http.get<CatalogItemViewResponseDto>(this.url(`/catalog/items/${id}`), { params });
  }

  /**
   * Отдельный folder-driven read-model для registry-экрана каталога.
   * Не переиспользует browse/detail endpoints каталога.
   */
  listRegistry(query: CatalogRegistryQuery) {
    const params = toCatalogRegistryHttpParams(query);
    return this.http.get<CatalogRegistryResponseDto>(this.url('/catalog/registry'), { params });
  }

  /**
   * Переносит выбранные элементы каталога в UI-папку или возвращает их в корень.
   * Backend сам резолвит дочерние серии к модели.
   */
  moveItems(body: CatalogItemsMoveRequestDto) {
    return this.http.post<CatalogItemsMoveResponseDto>(this.url('/catalog/items/move'), body);
  }

  /**
   * Меняет ручной порядок одного элемента смешанного списка каталога.
   * Frontend передает только перемещаемый элемент и соседей, не весь sibling scope.
   */
  moveOrder(body: CatalogOrderMoveRequestDto) {
    return this.http.post<CatalogOrderMoveResponseDto>(this.url('/catalog/order/move'), body);
  }

  /**
   * Список счетов учета с серверным поиском и фильтрацией по публикации.
   */
  listAccounts(query: CatalogAccountsQuery = {}) {
    const params = toHttpParams(query as Record<string, unknown>);
    return this.http.get<CatalogAccountListResponseDto>(this.url('/catalog/accounts'), { params });
  }

  /**
   * Обновляет флаг публикации одного счета учета.
   */
  patchAccount(id: Uuid, body: CatalogAccountPatchRequestDto) {
    return this.http.patch<CatalogAccountDto>(this.url(`/catalog/accounts/${id}`), body);
  }

  /**
   * Список видов номенклатуры с серверным поиском и фильтрацией локальных sync-настроек.
   */
  listItemTypes(query: CatalogItemTypesQuery = {}) {
    const params = toHttpParams(query as Record<string, unknown>);
    return this.http.get<CatalogItemTypeListResponseDto>(this.url('/catalog/item-types'), { params });
  }

  /**
   * Обновляет локальные sync-настройки одного вида номенклатуры.
   */
  patchItemType(id: Uuid, body: CatalogItemTypePatchRequestDto) {
    return this.http.patch<CatalogItemTypeDto>(this.url(`/catalog/item-types/${id}`), body);
  }

  /**
   * Справочник складов каталога.
   * Нужен для user-facing select'ов, где вместо ввода UUID показываем понятный список.
   */
  listCatalogWarehouses(query: CatalogWarehousesQuery = {}) {
    const params = toHttpParams(query as Record<string, unknown>);
    return this.http.get<CatalogWarehouseListResponseDto>(this.url('/catalog/warehouses'), { params });
  }

  patchCatalogWarehouse(id: Uuid, body: CatalogWarehousePatchRequestDto) {
    return this.http.patch<CatalogWarehouseDto>(this.url(`/catalog/warehouses/${id}`), body);
  }

  listPriceTypes() {
    return this.http.get<CatalogPriceTypeListResponseDto>(this.url('/catalog/price-types'));
  }

  patchPriceType(id: Uuid, body: CatalogPriceTypePatchRequestDto) {
    return this.http.patch<CatalogPriceTypeDto>(this.url(`/catalog/price-types/${id}`), body);
  }

  // Каталог папок.

  /**
   * Возвращает дерево или список папок каталога.
   */
  listFolders() {
    return this.http.get<CatalogFolderListResponseDto>(this.url('/catalog/folders'));
  }

  /**
   * Возвращает одну папку по идентификатору.
   */
  viewFolder(id: Uuid) {
    return this.http.get<CatalogFolderDto>(this.url(`/catalog/folders/${id}`));
  }

  /**
   * Создаёт папку каталога.
   */
  createFolder(body: CatalogFolderCreateRequestDto) {
    return this.http.post<CatalogFolderDto>(this.url('/catalog/folders'), body);
  }

  /**
   * Обновляет свойства папки каталога.
   */
  patchFolder(id: Uuid, body: CatalogFolderPatchRequestDto) {
    return this.http.patch<CatalogFolderDto>(this.url(`/catalog/folders/${id}`), body);
  }

  /**
   * Удаляет папку каталога.
   */
  deleteFolder(id: Uuid) {
    return this.http.delete<{ ok: boolean }>(this.url(`/catalog/folders/${id}`));
  }

  // Внутренние утилиты.

  private url(path: string): string {
    return buildApiUrl(this.apiBaseUrl, path);
  }

  /**
   * Оставлено для совместимости с auth-слоем.
   * Пригодится, если в каталоге появятся публичные или bootstrap-endpoint'ы,
   * которые должны обходить стандартный auth interceptor.
   */
  private skipAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ 'X-Skip-Auth': '1' });
  }
}
